import {
	PrepopulatedCredentials,
	UserAccountData,
	WorkspaceConfiguration,
} from '@citrite/workspace-ui-platform';
import $ from 'jquery';
import { performance } from 'Components/performance';
import { originalHash } from 'Environment';
import { isDesktop, isMobileOrTabletDevice } from 'Environment/launchResource/device';
import { getFromLocalStorage } from 'javascript/sf/Storage';
import { isHTML5ShieldEnabled } from 'LeaseWorkflow/ShieldModule';
import { resumeFormsToken } from 'Login/constants';
import { setWebViewLogon } from 'Login/DSAuth/authentication';
import { isChromeAppCompatible } from 'Workspace/ChromeApp/ChromeAppHelperMethods';
import { lastUserDetailsKey } from 'Workspace/UserContext/constants';
import { AuthExtensions, CredentialHandler } from './authExtensions';
import { FormActions } from './formActions';
import { generateLoginFormFromXml } from './generateLoginForm';

export interface AuthenticationResult {
	authenticationType: string;
	isPasswordChangeAllowed: boolean;
	isPasswordExpiryNotificationEnabled?: boolean;
	passwordExpiryTime?: number;
	webLogoffUrl?: string;
	authenticationResponse?: Element;
}

export function registerJQueryUI() {
	require('jquery-ui');
}

type UserDetailsCacheEntry = Pick<UserAccountData, 'userId' | 'userDisplayName'>;

export function registerFormsAuthenticationWidget() {
	const chimeraCancelBtnId = 'chimeraCancelBtn';
	const browserTextInputEvent =
		'oninput' in $("<input type='text'/>")[0] ? 'input' : 'keyup';
	const authenticationHeaders = {
		'X-Citrix-AM-CredentialTypes':
			'none, username, domain, password, newpassword, passcode, savecredentials, textcredential, webview',
		'X-Citrix-AM-LabelTypes':
			'none, plain, heading, information, warning, error, confirmation, image',
	};

	const enterKey = 13;
	const escapeKey = 27;

	//tslint:disable:function-name

	const formsAuthenticationWidget = {
		element: undefined as JQuery,
		stateContext: undefined as string,
		_trigger: undefined as any,
		_super: undefined as any,
		options: {
			allowCancel: true, // Whether generated forms should allow cancellation
			prePopulatedCredentials: {} as PrepopulatedCredentials,
			formActionSpinnerImageUrl: 'receiver/images/common/authspinner.gif',
			informationIconUrl: 'receiver/images/common/info.svg',
			reservedSpace: 0,
			// Responsible for sending form data and retrieving the next form
			// Callback function expects XML response data and (optional) name of explicit auth protocol
			formProvider(
				_url: string,
				_formData: any,
				_headers: object,
				callback: (xml: Element, name: string) => void
			) {
				callback(null, '');
			},
			// Event callbacks
			beforeDisplayForm: null as Function, // Form UI is ready to be displayed
			displayForm: null as Function, // Form UI has been displayed
			authenticationSuccess: null as Function,
			authenticationCancel: null as Function,
			authenticationError: null as Function,
			workspaceConfiguration: null as WorkspaceConfiguration,
			showFormContainer: () => {},
		},

		_create() {
			// CTXS.AssistiveTextGenerator.informationIconUrl = this.options.informationIconUrl;
		},

		_setOption(key: string, value: any) {
			if (key === 'informationIconUrl') {
				// CTXS.AssistiveTextGenerator.informationIconUrl = value;
			}
			this._super(key, value);
		},

		startAuthentication(initialUrl: string) {
			const self = this;
			const headers =
				self._updateCredentialAndLabelTypesForHeaders(authenticationHeaders);

			// Retrieve the logon form from the external provider
			self.options.formProvider(initialUrl, {}, headers, self._getResponseHandler());
		},

		resumeForms(resumeUrl: string, stateContext: string, credentialId: string) {
			const formData = 'StateContext=' + stateContext + '&' + credentialId + '=';
			const headers =
				this._updateCredentialAndLabelTypesForHeaders(authenticationHeaders);
			this.options.formProvider(resumeUrl, formData, headers, this._getResponseHandler());
		},

		_getResponseHandler() {
			return this._processResponse.bind(this);
		},

		// This function processes the results of the form post and get events.
		_processResponse(xmlData: Element, explicitAuthProtocolName: string) {
			const self = this;
			const clientResult = $('Result', xmlData).text();

			$('body').css('cursor', 'default');

			switch (clientResult) {
				case 'success':
					const isWebViewLogin = originalHash && originalHash.includes(resumeFormsToken);
					setWebViewLogon(isWebViewLogin);
					const authenticationResult: AuthenticationResult = {
						authenticationType: $('AuthType', xmlData).text(),
						isPasswordChangeAllowed:
							$('IsChangePasswordEnabled', xmlData).text() === 'true',
						isPasswordExpiryNotificationEnabled:
							$('IsExpiryNotificationEnabled', xmlData).text() === 'true',
						passwordExpiryTime: parseInt($('TimeRemaining', xmlData).text(), 10) || 0,
						webLogoffUrl: findFirstNamespacedChildNodeValue(
							xmlData,
							'webLogoffUrl',
							'http://citrix.com/delivery-services/webAPI/2-6/weblogoff'
						),
						authenticationResponse: xmlData,
					};

					self._trigger('authenticationSuccess', null, authenticationResult);
					break;
				case 'cancelled':
					self._trigger('authenticationCancel', null, {
						authenticationResponse: xmlData,
					});
					break;
				case 'fail':
					const authResult = {
						errorId: $('LogMessage', xmlData).text(),
						authenticationResponse: xmlData,
					};
					self._trigger('authenticationError', null, authResult);
					break;
				case 'more-info':
				case 'update-credentials':
					/**When shield enable and offline resource is available
					Send dumpy request to test Athena reachable status
					It will increase normal login time for around 177ms when Athena up
					It will show offline page for around 320ms when Athena down
					Basically it tooks 8-9s for end user to see the login screen after click chromeapp icon
					**/
					if (
						isChromeAppCompatible(this.options.workspaceConfiguration) ||
						isHTML5ShieldEnabled(this.options.workspaceConfiguration)
					) {
						const initialUserDetailsFromLocalStorage =
							getFromLocalStorage<UserDetailsCacheEntry>(lastUserDetailsKey);
						//Not first time login
						if (initialUserDetailsFromLocalStorage) {
							const startURL = $('Credential', xmlData)
								.children()
								.eq(2)
								.children()
								.eq(0)
								.text();
							//Only check for stores that using Athena service
							if (
								typeof startURL === 'string' &&
								startURL.includes('accounts-dsauthweb')
							) {
								/**Here:
								tried to use fetch instead of XMLHttpRequest, but failed with CORS
								"TypeError: Failed to fetch", with this error, program not proceed
								XMLHttpRequest meet CORS issues, too, but it won't block program
								**/
								const xhr = new XMLHttpRequest();
								xhr.onreadystatechange = function () {
									if (xhr.readyState === 4) {
										if (xhr.status === 404 || xhr.status === 503) {
											//CVADHELP-21449: Failed to load vanity URL sites in CWA ChromeApp
											//because of HEAD request returning a 500 error.
											//Since the use case is to check for Athena server down,
											//404 and error code more than 500 makes more sense for the check.
											const errorDetails = {
												errorId: 'athenabrokedown',
											};
											self._trigger('authenticationError', null, errorDetails);
										} else {
											self.stateContext = $('StateContext', xmlData).text();
											self._displayResponseContent(xmlData, explicitAuthProtocolName);
										}
									}
								};
								//Note DSAuthWeb2 has an endpoint specifically designed to test whether it is up or not: https://accounts-dsauthweb.cloud.com/status/connect
								xhr.open(
									'GET',
									'https://accounts-dsauthweb.cloud.com/status/connect',
									true
								);
								xhr.send(null);
								break;
							}
						}
					}
					self.stateContext = $('StateContext', xmlData).text();
					self._displayResponseContent(xmlData, explicitAuthProtocolName);
					break;
				default:
					break;
			}
		},

		_displayResponseContent(xmlData: Element, explicitAuthProtocolName: string) {
			const self = this;
			const $form = generateLoginFormFromXml(
				xmlData,
				self.options.prePopulatedCredentials,
				this.options.workspaceConfiguration
			);

			self._trigger('beforeDisplayForm');

			self.element.html($form as any);
			self._formatForm(xmlData, explicitAuthProtocolName);

			// Allow external code to modify the UI now that it has been displayed
			self._trigger('displayForm', null, { authenticationElement: self.element });

			// Run any auto post functions registered.
			if (AuthExtensions.isAutoPostAddedToForm()) {
				AuthExtensions.AutoPostCallback(self.element);
				return;
			}

			this.options.showFormContainer();

			if (isDesktop()) {
				self._focusOnFirstInputField();
			}

			self._reinforceAlerts();

			// Add scrollbar before new form is loaded. Scrollbar is only added when creator of the widget has passed reservedSpace
			// Reserved space is used to calculate space available for control, if space available is less than needed then scrollbar is presented on screen.
			if (self.options.reservedSpace) {
				self._addScrollBar();
			}

			if (window.matchMedia) {
				const mql = window.matchMedia('all and (max-width: 767px)');

				const handleMediaChange = function (
					mediaQueryList: Pick<MediaQueryList, 'matches'>
				) {
					if (mediaQueryList.matches && isMobileOrTabletDevice()) {
						$('.informationAssistive').ctxsAddCustomTooltip(
							$('.informationAssistiveTooltip'),
							$('.form-container')
						);
					}
				};

				mql.addListener(handleMediaChange);
				handleMediaChange(mql);
			}
		},

		_focusOnFirstInputField() {
			const self = this;
			const hasErrorOrEmpty = function (this: HTMLElement) {
				return $(this).val() === '' || $(this).hasClass('highlightedInput');
			};
			let fieldsToSelect = self.element.find('input:enabled').filter(hasErrorOrEmpty);

			if (!fieldsToSelect || fieldsToSelect.length === 0) {
				fieldsToSelect = self.element.find('input:enabled, .button');
			}
			if (fieldsToSelect) {
				fieldsToSelect.first().focus();
				const isButton = fieldsToSelect.first().hasClass('button');
				if (!isButton) {
					fieldsToSelect.first().select();
				}
			}
		},

		// Both header types done at once as it's unlikely for only one header to require custom items.
		_updateCredentialAndLabelTypesForHeaders(headers: object) {
			// Use extend to enforce pass-by-value behaviour, leaving default headers unchanged.
			let modifiedHeaders = $.extend({}, headers);
			// Add in custom credential and label types
			modifiedHeaders = this._addCustomFormTypesToHeaders(
				modifiedHeaders,
				'X-Citrix-AM-LabelTypes',
				/* CTXS.AuthExtensions.getLabelHandlers() */ []
			);
			modifiedHeaders = this._addCustomFormTypesToHeaders(
				modifiedHeaders,
				'X-Citrix-AM-CredentialTypes',
				/* CTXS.AuthExtensions.getCredentialHandlers() */ []
			);
			return modifiedHeaders;
		},

		_getTypeName(handler: CredentialHandler, headerName: string) {
			switch (headerName) {
				case 'X-Citrix-AM-CredentialTypes':
					return handler.getCredentialTypeName();
				default:
					return '';
			}
		},

		_addCustomFormTypesToHeaders(
			headers: Record<string, string>,
			headerName: string,
			customItemHandlers: CredentialHandler[]
		) {
			// Append the custom credential/label types to the headers as required.
			let advertisedItems = headers[headerName];
			if (customItemHandlers) {
				if (!advertisedItems) {
					advertisedItems = '';
				}
				for (let index = 0; index < customItemHandlers.length; index++) {
					const handler = customItemHandlers[index];
					const typeName = this._getTypeName(handler, headerName);
					if (typeName && typeof typeName === 'string') {
						const separator = advertisedItems ? ', ' : '';
						advertisedItems += separator + typeName;
					}
				}
				headers[headerName] = advertisedItems;
			}
			return headers;
		},

		_formatForm(xmlData: Element, explicitAuthProtocolName: string) {
			const self = this;

			self.element
				.find('.form')
				.addClass('credentialform')
				.append('<div class="spacer"/>');
			self.element.find('form').attr('method', 'POST');
			self.element.find('form').attr('action', $('PostBack', xmlData).text());

			self.element.find('.CredentialTypesavecredentials').hide();

			if (!self.options.allowCancel) {
				self.element.find('#' + chimeraCancelBtnId).remove();
			}

			self.element.find('.CheckMeWhenInDom').attr('checked', true as any);

			if (explicitAuthProtocolName === 'CustomForms') {
				self.element.find('.form').addClass('customform');
			}

			// Checking for all input divs, if the left div contains an empty label for all the inputs then remove them and left align the inputs
			// This is to fix "Verify your identify" form in the Risk Based Authentication feature.
			function correctAlignmentOfUIWithEmptyLabels($form: JQuery) {
				const $inputDivs = $form.find(
					"div[class*='CredentialType']:not(.CredentialTypenone)"
				);
				const isEmptyLabel = function (this: Element) {
					return $(this).text() === '';
				};

				// If the label is empty then up the count.
				const emptyLabelCount = $inputDivs
					.find('.left label')
					.filter(isEmptyLabel).length;

				// If the count of empty labels = the number of input divs then remove
				// all the left divs containing the empty labels and left align the inputs
				if (emptyLabelCount === $inputDivs.length) {
					$inputDivs.each(function (this: Element) {
						$(this).find('.left').remove();
						$(this).find('.right').removeClass('right').addClass('left');
					});
				}
			}

			correctAlignmentOfUIWithEmptyLabels(self.element.find('form'));

			// For each text box element that has assistive text, add the assistive text as a placeholder attribute
			self.element.find('.inlineAssistiveText').each(function (this: Element) {
				const assistiveDiv = $(this);
				const parentDiv = assistiveDiv.parent();
				// Find the input
				const input = parentDiv.children('input[type=text], input[type=password]');

				// Assistive text is currently only displayed for input fields.
				if (input.length !== 1) {
					return;
				}

				input.attr('placeholder', assistiveDiv.text()).attr('title', assistiveDiv.text());

				assistiveDiv.remove();
			});

			// Style forms buttons as links if the credential id begins with "link_"
			self.element
				.find('.forms-authentication-button[id^=link_]')
				.addClass('display-as-link authentication-link');

			// Style forms buttons differently according to whether they are the only buttons in the row
			self.element
				.find('.buttonscontainer')
				.filter(function (this: HTMLElement) {
					return (
						$(this).find('.forms-authentication-button').not('.display-as-link').length >
						1
					);
				})
				.addClass('multiple-buttons');

			// IE8 does not support the last-child CSS selector, so a CSS class is used instead
			self.element
				.find('.forms-authentication-button:last-child')
				.not('.display-as-link')
				.addClass('last-child');

			const getButtonInfo = function ($button: JQuery) {
				const buttonId = $button.attr('id');
				const buttonLabel = $button.text();

				return buttonId && buttonLabel
					? {
							id: buttonId,
							value: buttonLabel,
					  }
					: null;
			};

			// Wire up the behaviour of the generated forms buttons
			self.element
				.find('.forms-authentication-button')
				.addClass('default') // We style all buttons the same because the forms protocol does not tell us which is a primary/default button
				.click(function (this: HTMLButtonElement, e) {
					e.preventDefault();
					performance.mark(performance.events.AuthStart_SelfHosted);
					const $this = $(this);
					const isDisabled = $this.hasClass('disabled') || $this.hasClass('waiting');
					const isCancelButton = $this.attr('id') === chimeraCancelBtnId;

					if (isDisabled) {
						return;
					}

					if (isCancelButton) {
						self.element.find('form').attr('action', $('CancelPostBack', xmlData).text());
					}
					self._submitForm(getButtonInfo($this));
				});

			const $username = self.element.find('.CredentialTypeusername input');

			function setDomainState() {
				const $domainFields = self.element.find(
					'.CredentialTypedomain input, .CredentialTypedomain select'
				);
				const $domainLabel = self.element.find('.CredentialTypedomain label');
				const disableDomain =
					$username.val().indexOf('@') !== -1 || $username.val().indexOf('\\') !== -1;

				$domainFields.prop('disabled', disableDomain);
				// A CSS class is used to disable the label, because IE8 overrides the styling when using the disabled attribute.
				if (disableDomain) {
					$domainLabel.addClass('disabled');
				} else {
					$domainLabel.removeClass('disabled');
				}
			}

			// Arrange for the domain field (if present) to be disabled when the user types a username containing a '\' or '@' symbol,
			// as these both signify the domain name is specified within the username field (SAM or UPN format, respectively).
			if (self.element.find('.CredentialTypedomain').length > 0) {
				$username.on('change ' + browserTextInputEvent, setDomainState);
				setDomainState();
			}

			self.element
				.find('input[type=text], input[type=password]')
				.attr('autocomplete', 'off')
				.attr('spellcheck', false as any);
			self._disableFormAutoSubmission();

			self.element.on('keydown', 'input, .button', $.proxy(self._handleKeydown, self));
		},

		_reinforceAlerts() {
			// Reapply text for the first alert message (ordered by severity), to trigger JAWS to read it.
			const self = this;
			const alertSelectors = ['.error', '.warning', '.information', '.confirmation'];
			for (let i = 0; i < alertSelectors.length; i++) {
				const $elt = self.element.find(alertSelectors[i]);
				if ($elt.length) {
					const $firstAlert = $elt.first();
					$firstAlert.text($firstAlert.text());
					break;
				}
			}
		},

		_handleKeydown(e: JQueryEventObject) {
			const self = this;
			const $target = $(e.target);
			let $buttonElement;

			if (e.keyCode === enterKey) {
				self.element.off('keydown', 'input, .button', self._handleKeydown);

				if (!$target.is('.button')) {
					// Assume the user wants to submit the form, so find the first forms auth button
					e.preventDefault();
					e.stopPropagation();
					$buttonElement = self.element
						.find('.form .forms-authentication-button')
						.first();
					$buttonElement.click();
				}
			} else if (e.keyCode === escapeKey) {
				$buttonElement = self.element.find('#' + chimeraCancelBtnId);
				if ($buttonElement.length) {
					self.element.off('keydown', 'input, .button', self._handleKeydown);
					e.preventDefault();
					e.stopPropagation();
					$buttonElement.click();
				}
			}
		},

		// Submits the form, after first disabling it and showing an hourglass cursor.
		_submitForm(buttonDetails: { id: string; value: string }) {
			const self = this;
			const $form = self.element.find('.form');
			const headers =
				self._updateCredentialAndLabelTypesForHeaders(authenticationHeaders);

			FormActions.disableFormAndShowHourglass($form, buttonDetails);

			// To accommodate height of spinner and make sure spinner is visible
			if (self.options.reservedSpace) {
				self._updateScrollHeight();
			}

			const postTarget = $form.attr('action');
			const formToPost = self._getFormPostData(buttonDetails);

			// Send the form to the external form data handler
			self.options.formProvider(
				postTarget,
				formToPost,
				headers,
				self._getResponseHandler()
			);
		},

		// Gets the form's data as a POST request.
		_getFormPostData(buttonDetails: { id: string; value: string }) {
			const self = this;
			const values: Record<string, string | string[] | boolean> = {};
			const form = self.element.find('.form');

			// Collect all non-button input values
			$("input[type!='submit']", form).each(function (this: HTMLInputElement) {
				let value;
				if (this.type === 'checkbox') {
					value = this.checked;
				} else if (this.type === 'radio') {
					if (this.checked) {
						value = this.value;
					}
				} else {
					value =
						this.name === 'username' || this.name === 'domain'
							? this.value.trim()
							: this.value;
				}
				if (typeof value !== 'undefined') {
					values[this.name] = value;
				}
			});

			if (values.username) {
				self.options.prePopulatedCredentials.username = values.username as string;
				self.options.prePopulatedCredentials.domain = values.domain as string;
				self.options.prePopulatedCredentials.password = ''; // Do not want to store password in program memory
			}

			// Collect all select values
			$('select', form).each(function (this: HTMLSelectElement) {
				const selectedValues = [];
				for (let i = 0; i < this.length; i++) {
					if (this.options[i].selected) {
						selectedValues.push(this.options[i].value);
					}
				}
				values[this.name] = selectedValues.length === 0 ? '' : selectedValues;
			});

			if (buttonDetails) {
				values[buttonDetails.id] = buttonDetails.value;
			}

			values.StateContext = self.stateContext;

			return $.param(values, true);
		},

		// Disables submit buttons in the form to prevent duplicate form submission, since the form is also submitted via the button's click handler.
		// Using the input button alone for form submission resulted in a problem where clicking towards the left/right edges of the overall sliding-doors
		// button had no effect since the input button is the "inner" part of the sliding door (it cannot be the outer part since no elements are permitted
		// inside an <input> element). For this reason, a click handler is instead added to the entire button and auto-submission disabled.
		_disableFormAutoSubmission() {
			this.element.find('.form').submit(function () {
				return false;
			});
		},

		_addScrollBar() {
			const self = this;
			const $logonboxScroller = self.element.find('.logonbox-scroller');
			if ($logonboxScroller.length === 0) {
				self.element
					.find('form.credentialform')
					.wrap('<div class="logonbox-scroller"></div>')
					.parent()
					.ctxsMakeScrollable();
				self._updateScrollHeight();
			}
		},

		_updateScrollHeight() {
			const self = this;
			const $logonboxScroller = self.element.find('.logonbox-scroller');
			const controlPaddingTop = parseFloat(self.element.css('paddingTop')) || 0;
			const controlPaddingBottom = parseFloat(self.element.css('paddingBottom')) || 0;
			const totalAvailableHeight = $(window).height();

			const spaceAvailable =
				totalAvailableHeight -
				self.options.reservedSpace -
				controlPaddingTop -
				controlPaddingBottom;
			const requiredHeight = self.element.find('form.credentialform').height();

			// 1 pixel works around in IE 10, without this scrollbar appears unnecessarily.
			const scrollHeight = Math.min(spaceAvailable, requiredHeight + 1);
			$logonboxScroller.height(scrollHeight);
			$logonboxScroller.ctxsUpdateScrollPane();
		},

		resize() {
			const self = this;
			if (self.options.reservedSpace) {
				self._updateScrollHeight();
			}
		},
	};

	function findFirstNamespacedChildNodeValue(
		element: Element,
		tagName: string,
		namespaceUri: string
	) {
		const nodes = element.getElementsByTagNameNS(namespaceUri, tagName);
		if (nodes.length >= 1) {
			return $(nodes[0]).text();
		}
		return null;
	}

	$.widget('ctxs.ctxsFormsAuthentication', formsAuthenticationWidget);
}
